import axiosInstance from './axiosInstance';
import { LicenceType, LicenceUsersQuery, SubscriptionPlan } from '@/types';

type MarkInvoicePaidPayload = {
    invoiceUuid: string;
    status: 'paid';
    paymentType: string;
    transactionReference: string;
    notes?: string;
    paymentDate: string;
};

export class SubscriptionService {
    static async getPlans(params?: any, signal?: AbortSignal): Promise<any> {
        try {
            const response = await axiosInstance.get('/subscription/get-all-plans', {
                params,
                signal
            });
            return response;
        } catch (error) {
            console.error('Error fetching subscription plans:', error);
            throw error;
        }
    }

    static async getPlanById(id: string | number): Promise<any> {
        try {
            const response = await axiosInstance.get(`/subscription/get-plan-details/${id}`);
            return response;
        } catch (error) {
            console.error('Error fetching subscription plan details:', error);
            throw error;
        }
    }

    static async createPlan(data: SubscriptionPlan): Promise<any> {
        try {
            const response = await axiosInstance.post('/subscription/create-plan', data); 
            return response;
        } catch (error) {
            console.error('Error creating subscription plan:', error);
            throw error;
        }
    }

    static async updatePlan(id: string | number, data: SubscriptionPlan): Promise<any> {
        try {
            const response = await axiosInstance.put(`/subscription/update-plan/${id}`, data);
            return response;
        } catch (error) {
            console.error('Error updating subscription plan:', error);
            throw error;
        }
    }

    static async deletePlan(id: string | number): Promise<any> {
        try {
            const response = await axiosInstance.delete(`/subscription/delete-plan/${id}`, { skipGlobalLoader: true });
            return response;
        } catch (error) {
            console.error('Error deleting subscription plan:', error);
            throw error;
        }
    }

    static async getActiveSubscriptions(params?: any, signal?: AbortSignal): Promise<any> {
        try {
            const response = await axiosInstance.get('/subscription/get-all-active-subscription', {
                params,
                signal,
            });
            return response;
        } catch (error) {
            console.error('Error fetching active subscriptions:', error);
            throw error;
        }
    }

    static async getInvoiceList(userId: number, params?: any, signal?: AbortSignal): Promise<any> {
        try {
            const response = await axiosInstance.get('/subscription/invoice-list', {
                params: { userId, ...params },
                signal,
            });
            return response;
        } catch (error) {
            console.error('Error fetching subscription invoice list:', error);
            throw error;
        }
    }

    static async getInvoiceDetails(invoiceUuid: string): Promise<any> {
        try {
            const response = await axiosInstance.get(`/subscription/get-invoice-details/${invoiceUuid}`);
            return response;
        } catch (error) {
            console.error('Error fetching subscription invoice details:', error);
            throw error;
        }
    }

    static async markInvoiceAsPaid(data: MarkInvoicePaidPayload): Promise<any> {
        try {
            const response = await axiosInstance.post('/subscription/manual-payment/mark-paid', data, { skipGlobalLoader: true });
            return response;
        } catch (error) {
            console.error('Error marking subscription invoice as paid:', error);
            throw error;
        }
    }

    static async getFeatures(): Promise<any> {
        try {
            const response = await axiosInstance.get('/subscription/package-master-description');
            return response;
        } catch (error) {
            console.error('Error fetching features:', error);
            throw error;
        }
    }

    static async getLicenceUsage(tenantId: number, signal?: AbortSignal): Promise<any> {
        try {
            return await axiosInstance.get(`/subscription/get-licence-usage/${tenantId}`, { signal });
        } catch (error) {
            console.error('Error fetching subscription licence usage:', error);
            throw error;
        }
    }

    static async getLicenceUsers(
        tenantId: number,
        licenceType: LicenceType,
        params: LicenceUsersQuery = {},
        signal?: AbortSignal,
    ): Promise<any> {
        try {
            return await axiosInstance.get(`/subscription/get-licence-users/${tenantId}`, {
                params: { ...params, licenceType },
                signal,
            });
        } catch (error) {
            console.error('Error fetching subscription licence users:', error);
            throw error;
        }
    }

    static async extendTrial(userId: number, days: number): Promise<any> {
        try {
            const response = await axiosInstance.post('/subscription/trial-extend', { userId, days }, { skipGlobalLoader: true });
            return response;
        } catch (error) {
            console.error('Error extending trial:', error);
            throw error;
        }
    }
}

export default SubscriptionService;
